/** The six brushes the classic editor had, kept as they were. */
export type BrushType = 'solid' | 'dashed' | 'dotted' | 'marker' | 'pencil' | 'airbrush'

export const BRUSH_TYPES: BrushType[] = ['solid', 'dashed', 'dotted', 'marker', 'pencil', 'airbrush']

/** A drawn line, in the percentage units every preview and the bake share. */
export interface BrushStroke {
  id: string
  /** Points as percentages of strip width / height. */
  points: { xPct: number; yPct: number }[]
  color: string
  /** Line width as a percentage of strip width. */
  sizePct: number
  brushType: BrushType
}

/**
 * The classic editor's size slider ran 1–50 px on its strip canvas. Sizes are
 * kept as that range, read against a strip this wide, so a "10" looks the
 * same on a 600 px preview and a 2400 px print.
 */
const REFERENCE_WIDTH = 1200
export const BRUSH_SIZE_MIN = 1
export const BRUSH_SIZE_MAX = 50
export const BRUSH_SIZE_DEFAULT = 10
export const brushSizePct = (size: number): number => (size / REFERENCE_WIDTH) * 100

/**
 * Draw one stroke on a canvas `width` x `height` — the on-screen layer and the
 * print both call this, so what the guest draws is what prints. The styling
 * per brush is the classic editor's, with one change: dashes and dots are
 * sized from the line's own width. Classic used fixed pixel minimums, so the
 * gaps were visible on the small screen canvas and closed up by the round
 * line ends at print resolution — a dashed line printed solid.
 */
export function drawStroke(
  ctx: CanvasRenderingContext2D,
  stroke: BrushStroke,
  width: number,
  height: number
): void {
  if (stroke.points.length === 0) return
  const size = (stroke.sizePct / 100) * width
  ctx.save()
  ctx.strokeStyle = stroke.color
  ctx.fillStyle = stroke.color
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  let lineWidth = size
  switch (stroke.brushType) {
    case 'dashed':
      // Round ends add half a width to each side of a dash: the gap allows for it.
      ctx.setLineDash([size * 1.6, size * 2.4])
      break
    case 'dotted':
      // A near-zero dash with round ends is a dot one line-width across.
      ctx.setLineDash([size * 0.01, size * 2])
      break
    case 'marker':
      ctx.globalAlpha = 0.6
      lineWidth = size * 1.8
      ctx.shadowBlur = lineWidth * 0.3
      ctx.shadowColor = stroke.color
      break
    case 'pencil':
      ctx.globalAlpha = 0.85
      lineWidth = size * 0.7
      ctx.shadowBlur = lineWidth * 0.2
      ctx.shadowColor = stroke.color
      break
    case 'airbrush':
      ctx.globalAlpha = 0.4
      lineWidth = size * 1.5
      ctx.shadowBlur = lineWidth * 0.5
      ctx.shadowColor = stroke.color
      break
    case 'solid':
    default:
      ctx.setLineDash([])
  }
  ctx.lineWidth = lineWidth

  const px = stroke.points.map((p) => ({ x: (p.xPct / 100) * width, y: (p.yPct / 100) * height }))
  if (px.length === 1) {
    // A tap is a dot, not nothing.
    ctx.beginPath()
    ctx.arc(px[0].x, px[0].y, lineWidth / 2, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()
    return
  }
  ctx.beginPath()
  ctx.moveTo(px[0].x, px[0].y)
  // Quadratic curves through the midpoints, as the classic editor drew them.
  for (let i = 1; i < px.length - 1; i++) {
    const mid = { x: (px[i].x + px[i + 1].x) / 2, y: (px[i].y + px[i + 1].y) / 2 }
    ctx.quadraticCurveTo(px[i].x, px[i].y, mid.x, mid.y)
  }
  ctx.lineTo(px[px.length - 1].x, px[px.length - 1].y)
  ctx.stroke()
  ctx.restore()
}
